'use client';

import React, { type FC, type ReactNode, type SelectHTMLAttributes } from 'react';
import { cx } from '../../../utils/cx';
import { SelectField, styles } from './SelectField';
import type { CommonProps, SelectItemType } from './Select';
import useId from '../../../utils/useId';

const SEMANTIC_NAME = 'RomaNativeSelect';

export type NativeSelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & {
    items?: SelectItemType[];
    leadingIcon?: FC | ReactNode;
} & CommonProps;

export const NativeSelect = ({
    label,
    hint,
    items = [],
    placeholder,
    tooltip,
    leadingIcon,
    className,
    size = 'md',
    ...props
}: NativeSelectProps) => {
    const uniqueId = useId();
    const selectId = `${uniqueId}-select`;
    const labelId = `${uniqueId}-label`;
    const hintId = `${uniqueId}-hint`;

    const placeholderLabel = typeof placeholder === 'string' ? placeholder : 'Select';
    const isInvalid =
        props['aria-invalid'] !== undefined && props['aria-invalid'] !== false && props['aria-invalid'] !== 'false';

    return (
        <SelectField
            label={label}
            hint={hint}
            tooltip={tooltip}
            labelFor={selectId}
            labelId={labelId}
            hintId={hintId}
            isRequired={props.required}
            isInvalid={isInvalid}
            isDisabled={props.disabled}
            size={size}
            leadingIcon={leadingIcon}
            className={cx(SEMANTIC_NAME, className)}
        >
            <select
                {...props}
                id={selectId}
                aria-describedby={hint ? hintId : undefined}
                aria-labelledby={labelId}
                className={cx(
                    styles.common.control,
                    styles.sizes[size].root,
                    styles.sizes[size].trailingPadding,
                    leadingIcon && styles.sizes[size].leadingPadding,
                )}
            >
                {placeholder && (
                    <option value="" disabled>
                        {placeholderLabel}
                    </option>
                )}

                {items.map((item) => (
                    <option key={item.id} value={item.id} disabled={item.isDisabled}>
                        {item.label}
                    </option>
                ))}
            </select>
        </SelectField>
    );
};

NativeSelect.displayName = SEMANTIC_NAME;
